// ─────────────────────────────────────────────────────────────────────────────
// Design Tokens — shared across all components
// ─────────────────────────────────────────────────────────────────────────────

export const C = {
  surface:  "#0B1120",
  card:     "#111827",
  card2:    "#1A2535",
  border:   "#1E2D42",
  primary:  "#C62828",
  primaryL: "#E53935",
  red:      "#EF5350",
  redMuted: "#4A1919",
  muted:    "#64748B",
  text:     "#F1F5F9",
  textSoft: "#94A3B8",
  blue:     "#1565C0",
  orange:   "#E65100",
  green:    "#2E7D32",
  greenL:   "#43A047",
};

export const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800;900&family=DM+Sans:wght@300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --surface: ${C.surface};
    --card: ${C.card};
    --card2: ${C.card2};
    --border: ${C.border};
    --primary: ${C.primary};
    --primaryL: ${C.primaryL};
    --red: ${C.red};
    --muted: ${C.muted};
    --text: ${C.text};
    --textSoft: ${C.textSoft};
  }

  html, body { height: 100%; background: var(--surface); color: var(--text); font-family: 'DM Sans', sans-serif; }

  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: var(--card); }
  ::-webkit-scrollbar-thumb { background: #263348; border-radius: 4px; }

  .syne { font-family: 'Syne', sans-serif; }

  @keyframes fadeUp   { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeIn   { from { opacity:0; } to { opacity:1; } }
  @keyframes pulse    { 0%,100% { transform:scale(1); } 50% { transform:scale(1.06); } }
  @keyframes spinRing { to { transform:rotate(360deg); } }
  @keyframes slideIn  { from { opacity:0; transform:translateX(-16px); } to { opacity:1; transform:translateX(0); } }

  .fade-up  { animation: fadeUp  .55s cubic-bezier(.16,1,.3,1) both; }
  .fade-in  { animation: fadeIn  .4s ease both; }
  .slide-in { animation: slideIn .4s ease both; }

  .inp {
    width: 100%; background: ${C.card2}; border: 1.5px solid ${C.border};
    border-radius: 12px; padding: 13px 14px 13px 44px; color: ${C.text};
    font-family: 'DM Sans', sans-serif; font-size: 14px; outline: none;
    transition: border-color .2s, box-shadow .2s;
  }
  .inp:focus { border-color: ${C.primary}; box-shadow: 0 0 0 3px rgba(198,40,40,.15); }
  .inp::placeholder { color: ${C.muted}; }
  .inp-wrap { position: relative; }
  .inp-icon { position:absolute; left:14px; top:50%; transform:translateY(-50%); color:${C.red}; font-size:16px; pointer-events:none; }
  .inp-label { display:block; font-size:12px; font-weight:600; color:${C.textSoft}; margin-bottom:6px; letter-spacing:.4px; }

  .btn-primary {
    width:100%; padding:14px; background: linear-gradient(135deg, ${C.primary}, ${C.primaryL});
    color:#fff; border:none; border-radius:12px; font-family:'DM Sans',sans-serif;
    font-size:15px; font-weight:700; cursor:pointer; letter-spacing:.3px;
    box-shadow: 0 8px 24px rgba(198,40,40,.35);
    transition: transform .15s, box-shadow .15s, opacity .15s;
  }
  .btn-primary:hover { transform:translateY(-1px); box-shadow:0 12px 28px rgba(198,40,40,.45); }
  .btn-primary:active { transform:translateY(0); }
  .btn-primary:disabled { opacity:.55; cursor:not-allowed; transform:none; }

  .btn-ghost {
    background:transparent; border:1.5px solid ${C.border}; color:${C.textSoft};
    padding:10px 18px; border-radius:10px; font-size:13px; font-weight:600;
    cursor:pointer; transition:all .2s; font-family:'DM Sans',sans-serif;
  }
  .btn-ghost:hover { border-color:${C.primary}; color:${C.text}; }

  .btn-sm {
    padding:8px 16px; background:rgba(198,40,40,.15); color:${C.red};
    border:1px solid rgba(198,40,40,.3); border-radius:8px; font-size:12px;
    font-weight:700; cursor:pointer; transition:all .2s; font-family:'DM Sans',sans-serif;
  }
  .btn-sm:hover { background:rgba(198,40,40,.25); }

  .card  { background:${C.card};  border:1px solid ${C.border}; border-radius:16px; }
  .card2 { background:${C.card2}; border:1px solid ${C.border}; border-radius:14px; }

  .badge { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:700; }

  .chip { padding:5px 14px; border-radius:20px; font-size:12px; font-weight:700; cursor:pointer; border:1.5px solid ${C.border}; background:${C.card2}; color:${C.muted}; transition:all .2s; }
  .chip.active { background:${C.primary}; border-color:${C.primary}; color:#fff; }

  .nav-item {
    display:flex; align-items:center; gap:12px; padding:11px 14px;
    border-radius:10px; cursor:pointer; transition:all .2s; color:${C.muted};
    font-size:14px; font-weight:500;
  }
  .nav-item:hover { background:rgba(198,40,40,.08); color:${C.text}; }
  .nav-item.active { background:rgba(198,40,40,.15); color:${C.red}; font-weight:700; border-left:3px solid ${C.primary}; }

  .toggle { position:relative; width:42px; height:24px; }
  .toggle input { opacity:0; width:0; height:0; }
  .toggle-slider { position:absolute; inset:0; background:#263348; border-radius:12px; cursor:pointer; transition:.3s; }
  .toggle-slider::before { content:''; position:absolute; height:18px; width:18px; left:3px; bottom:3px; background:#64748b; border-radius:50%; transition:.3s; }
  .toggle input:checked + .toggle-slider { background:rgba(198,40,40,.3); }
  .toggle input:checked + .toggle-slider::before { transform:translateX(18px); background:${C.primary}; }

  table { width:100%; border-collapse:collapse; }
  thead th { text-align:left; padding:10px 14px; font-size:11px; font-weight:700; color:${C.muted}; text-transform:uppercase; letter-spacing:.8px; border-bottom:1px solid ${C.border}; }
  tbody td { padding:13px 14px; font-size:13px; border-bottom:1px solid rgba(30,45,66,.6); vertical-align:middle; }
  tbody tr:last-child td { border-bottom:none; }
  tbody tr:hover td { background:rgba(255,255,255,.02); }

  .spinner { width:20px; height:20px; border:2.5px solid rgba(255,255,255,.2); border-top-color:#fff; border-radius:50%; animation:spinRing .7s linear infinite; }

  .progress-track { height:6px; border-radius:4px; background:rgba(255,255,255,.07); overflow:hidden; }
  .progress-fill { height:100%; border-radius:4px; transition:width .4s ease; }

  select.inp { padding-left:14px; appearance:none; cursor:pointer; }
  textarea.inp { padding:14px; resize:vertical; min-height:100px; }

  .overlay { position:fixed; inset:0; background:rgba(0,0,0,.7); backdrop-filter:blur(4px); z-index:100; display:flex; align-items:center; justify-content:center; animation:fadeIn .2s ease;}
  .modal { background:${C.card}; border:1px solid ${C.border}; border-radius:20px; padding:28px; width:min(520px,95vw); max-height:70vh; overflow-y:auto; animation:fadeUp .3s ease; margin-top:200px; }

  .notif-dot { width:8px; height:8px; background:#FFD600; border-radius:50%; position:absolute; top:6px; right:6px; }

  @media (max-width:768px) {
    .sidebar { transform:translateX(-100%); position:fixed !important; z-index:50; transition:transform .3s; }
    .sidebar.open { transform:translateX(0); }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Utility color helpers
// ─────────────────────────────────────────────────────────────────────────────
export const levelColor   = (l) => ({ critical:"#D32F2F", low:"#F57C00", normal:"#1976D2", high:"#388E3C" }[l] || "#888");
export const urgencyColor = (u) => ({ Emergency:"#D32F2F", Urgent:"#F57C00", Scheduled:"#1976D2" }[u] || "#888");
export const statusColor  = (s) => ({ pending:"#F57C00", fulfilled:"#388E3C", cancelled:"#D32F2F" }[s] || "#888");
export const statusBg     = (s) => ({ pending:"rgba(245,124,0,.12)", fulfilled:"rgba(56,142,60,.12)", cancelled:"rgba(211,47,47,.12)" }[s] || "");